/**
 * chartHelpers.js
 * ---------------------------------------------------------------------------
 * Abhängigkeitsfreie SVG-/HTML-Diagrammbausteine für Dashboard-Widgets.
 * Jede Funktion ist rein: Daten rein → Markup-String raus. So lässt sie sich
 * direkt in BaseWidget.onRender() verwenden:
 *
 *     import { pie, euro } from './chartHelpers.js';
 *     this.bodyEl.innerHTML = pie(data, { summaryLabel: 'Lohnkosten / Monat' });
 *
 * WICHTIG (SVG-Fallstrick): Füllung/Kontur in SVG immer per CSS setzen
 * (class oder inline style="fill:…"), NICHT per Präsentationsattribut
 * fill="…". CSS-Variablen (var(--token)) lösen in SVG-Attributen NICHT auf –
 * deshalb stehen hier feste Hex-Werte aus dem neolohn-Token-Set.
 * ---------------------------------------------------------------------------
 */

/* Kategoriale Blau-Rampe (Kreis/Donut/Balken mit gleichartigen Kategorien). */
export const PALETTE = ['#273e69', '#2a6fdb', '#4aa3df', '#7cc1e8', '#b9def0'];

/* Klar unterscheidbare Flächen (Kreis nach Bereich): Blau-Rampe + Akzente. */
export const PIE_PALETTE = ['#273e69', '#2a6fdb', '#4aa3df', '#44c38a', '#f2c34d', '#FF914D'];

/* Semantische Farben aus dem Designsystem. */
export const C_BLUE = '#2a6fdb';
export const C_RED = '#e35b6a';
export const C_GREEN = '#44c38a';
export const C_YELLOW = '#f2c34d';

/* Zahl im deutschen Format mit Euro, geschütztes Leerzeichen vor €. */
export function euro(n, options = {}) { return n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', ...options }); }

/* Kompakte Darstellung f\u00fcr enge Achsenbeschriftungen: 12.345 -> "12 T \u20ac",
 * 1.234.567 -> "1,2 Mio \u20ac". Verhindert abgeschnittene f\u00fcnfstellige Labels.
 */
export function euroCompact(n) {
	const abs = Math.abs(n);
	if (abs >= 1_000_000) {
		const v = n / 1_000_000;
		const txt = (Math.round(v * 10) / 10).toLocaleString('de-DE');
		return `${txt}\u00a0Mio\u00a0\u20ac`;
	}
	if (abs >= 1_000) {
		const v = n / 1_000;
		const txt = (abs >= 10_000 ? Math.round(v) : Math.round(v * 10) / 10).toLocaleString('de-DE');
		return `${txt}\u00a0T\u00a0\u20ac`;
	}
	return `${Math.round(n).toLocaleString('de-DE')}\u00a0\u20ac`;
}

/* Reine Zahl im deutschen Format. */
export function num(n) { return n.toLocaleString('de-DE'); }

/* ───────────────────────── KREIS (gefüllte Segmente) ───────────────────── */
/**
 * @param {{label:string, value:number}[]} data
 * @param {object} [opts]
 * @param {string} [opts.summaryLabel]  Text rechts neben der Summe.
 * @param {(n:number)=>string} [opts.format]  Formatter für Summe + Legende.
 * @param {string[]} [opts.palette]
 * @param {boolean} [opts.showSummary=true]  Wenn false, wird die Summenzeile
 *   nicht gerendert (Caller blendet die Summe selbst woanders ein).
 */
export function pie(data, { summaryLabel = 'Gesamt', format = euro, palette = PIE_PALETTE, showSummary = true } = {}) {
	const cx = 70, cy = 70, r = 64;
	const total = data.reduce((s, d) => s + d.value, 0) || 1;
	// Wedges nur als Fläche zeichnen (keine Wedge-Strokes) — Separator und
	// Außenring kommen separat darüber. So überlagern sich an den Schnittpunkten
	// keine Miter-Spitzen und benachbarte Stroke-Kanten doppelt.
	const wedgePaths = [];
	const pctLabels = [];
	const seps = [];
	let a0 = -Math.PI / 2; // oben starten
	data.forEach((d, i) => {
		const frac = d.value / total;
		const color = palette[i % palette.length];

		// FIX: If there is only 1 item (or it takes up 100%), draw a full circle instead of a broken arc
		if (frac >= 0.9999) {
			wedgePaths.push(`<circle cx="${cx}" cy="${cy}" r="${r}" style="fill:${color}"></circle>`);
		} else {
			const a1 = a0 + frac * 2 * Math.PI;
			const x0 = cx + r * Math.cos(a0), y0 = cy + r * Math.sin(a0);
			const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
			const large = frac > 0.5 ? 1 : 0;
			const path = `M${cx} ${cy} L${x0.toFixed(2)} ${y0.toFixed(2)} A${r} ${r} 0 ${large} 1 ${x1.toFixed(2)} ${y1.toFixed(2)} Z`;
			
			wedgePaths.push(`<path d="${path}" style="fill:${color}"></path>`);
			a0 = a1; // Only need to advance the angle if it's not a single full circle
		}

		// Percentage Label
		if (frac >= 0.07) {
			// For a full circle, center the text perfectly in the middle, otherwise calculate wedge midpoint
			let lx = cx, ly = cy;
			if (frac < 0.9999) {
				const a1 = a0; // a0 has already been updated to the end of this wedge
				const prevA0 = a1 - frac * 2 * Math.PI;
				const mid = (prevA0 + a1) / 2, lr = r * 0.6;
				lx = cx + lr * Math.cos(mid);
				ly = cy + lr * Math.sin(mid);
			}
			pctLabels.push(`<text class="pie-pct" x="${lx.toFixed(1)}" y="${ly.toFixed(1)}" text-anchor="middle" dominant-baseline="middle">${Math.round(frac * 100)}%</text>`);
		}

		// Separators (only if there are multiple segments)
		if (data.length > 1) {
			const x0 = cx + r * Math.cos(a0 - frac * 2 * Math.PI), y0 = cy + r * Math.sin(a0 - frac * 2 * Math.PI);
			seps.push(`<line x1="${cx}" y1="${cy}" x2="${x0.toFixed(2)}" y2="${y0.toFixed(2)}" style="stroke:#fff" stroke-width="1.5" stroke-linecap="round"></line>`);
		}
	});
	const outerRing = `<circle cx="${cx}" cy="${cy}" r="${r}" style="fill:none;stroke:#fff" stroke-width="1.5"></circle>`;
	const wedges = `${wedgePaths.join('')}${seps.join('')}${outerRing}${pctLabels.join('')}`;
	const legend = data.map((d, i) =>
		`<span title="${d.label}"><span class="dot" style="background:${palette[i % palette.length]}"></span>${d.label.substring(0, 24)} <span class="val">${format(d.value)}</span></span>`
	).join('');
	const summary = showSummary
		? `<div class="chart-summary"><span class="big">${format(total)}</span><span class="sub">${summaryLabel}</span></div>`
		: '';
	return `
		${summary}
		<div class="chart-wrap">
			<svg class="chart-svg" viewBox="0 0 140 140" preserveAspectRatio="xMidYMid meet">${wedges}</svg>
			<div class="chart-legend">${legend}</div>
		</div>`;
}

/* ───────────────────────────── DONUT (Ring) ────────────────────────────── */
/**
 * @param {{label:string, value:number}[]} data
 * @param {object} [opts]
 * @param {string} [opts.centerLabel]   kleiner Text unter der Mittenzahl
 * @param {string} [opts.summaryLabel]
 * @param {string[]} [opts.palette]
 */
export function donut(data, { centerLabel = '', summaryLabel = 'Gesamt', palette = PALETTE } = {}) {
	const r = 52, cx = 70, cy = 70, C = 2 * Math.PI * r;
	const total = data.reduce((s, d) => s + d.value, 0) || 1;
	let off = 0;
	const segs = data.map((d, i) => {
		const len = (d.value / total) * C;
		const seg = `<circle cx="${cx}" cy="${cy}" r="${r}" stroke-width="18"
			style="fill:none;stroke:${palette[i % palette.length]}"
			stroke-dasharray="${len} ${C - len}" stroke-dashoffset="${-off}"
			transform="rotate(-90 ${cx} ${cy})" stroke-linecap="butt"></circle>`;
		off += len;
		return seg;
	}).join('');
	const legend = data.map((d, i) =>
		`<span title="${d.label}"><span class="dot" style="background:${palette[i % palette.length]}"></span>${d.label.substring(0, 24)} <span class="val">${num(d.value)}</span></span>`
	).join('');
	return `
		<div class="chart-summary"><span class="big">${num(total)}</span><span class="sub">${summaryLabel}</span></div>
		<div class="chart-wrap">
			<svg class="chart-svg" viewBox="0 0 140 140" preserveAspectRatio="xMidYMid meet">
				${segs}
				<text class="donut-center-num" x="70" y="68" text-anchor="middle" dominant-baseline="middle">${num(total)}</text>
				<text class="donut-center-lbl" x="70" y="84" text-anchor="middle">${centerLabel}</text>
			</svg>
			<div class="chart-legend">${legend}</div>
		</div>`;
}

/* ───────────────────────── VERTIKALE BALKEN ────────────────────────────── */
/**
 * @param {{label:string, value:number}[]} data
 * @param {object} [opts]
 * @param {string|number} [opts.summary]   große Kennzahl (Default: Summe)
 * @param {string} [opts.summaryLabel]
 * @param {string} [opts.delta]            z.B. "+21%" (optional)
 * @param {'up'|'down'} [opts.deltaDir]
 * @param {boolean} [opts.highlightLast]   letzten Balken rot hervorheben
 */
export function bars(data, { summary, summaryLabel = '', delta, deltaDir = 'up', highlightLast = false } = {}) {
	const W = 320, H = 150, padL = 26, padB = 22, padT = 8;
	const max = Math.max(...data.map(d => d.value), 1);
	const niceMax = Math.ceil(max / 5) * 5 || 5;
	const innerW = W - padL, innerH = H - padB - padT;
	const bw = innerW / data.length;
	const grid = [0, .25, .5, .75, 1].map(t => {
		const y = padT + innerH * (1 - t);
		return `<line class="grid-line" x1="${padL}" y1="${y}" x2="${W}" y2="${y}"/>
				<text class="axis-label" x="${padL - 6}" y="${y + 3}" text-anchor="end">${Math.round(niceMax * t)}</text>`;
	}).join('');
	const rects = data.map((d, i) => {
		const h = innerH * (d.value / niceMax);
		const x = padL + i * bw + bw * 0.18;
		const w = bw * 0.64;
		const y = padT + innerH - h;
		const fill = (highlightLast && i === data.length - 1) ? C_RED : C_BLUE;
		return `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="3" style="fill:${fill}"></rect>
				<text class="axis-label" x="${x + w / 2}" y="${H - 7}" text-anchor="middle">${d.label}</text>`;
	}).join('');
	const total = summary !== undefined ? summary : data.reduce((s, d) => s + d.value, 0);
	const deltaHtml = delta
		? `<span class="delta ${deltaDir === 'down' ? 'down' : 'up'}"><i class="bi bi-arrow-${deltaDir === 'down' ? 'down' : 'up'}-short"></i> ${delta}</span>`
		: '';
	return `
		<div class="chart-summary"><span class="big">${total}</span>${deltaHtml}<span class="sub">${summaryLabel}</span></div>
		<div class="chart-wrap">
			<svg class="chart-svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid meet">${grid}${rects}</svg>
		</div>`;
}

/* ───────────────────────── LINIE / FLÄCHE ──────────────────────────────── */
/**
 * @param {{label:string, value:number}[]} data
 * @param {object} [opts]
 * @param {string|number} [opts.summary]   große Kennzahl (Default: letzter Wert)
 * @param {string} [opts.summaryLabel]
 * @param {string} [opts.delta]            optional
 * @param {'up'|'down'} [opts.deltaDir]
 */
export function area(data, { summary, summaryLabel = '', delta, deltaDir = 'up' } = {}) {
	const W = 320, H = 150, padL = 28, padB = 22, padT = 10;
	const vals = data.map(d => d.value);
	const min = Math.min(...vals) - 4, max = Math.max(...vals) + 4;
	const innerW = W - padL - 6, innerH = H - padB - padT;
	const X = i => padL + (innerW * i) / (data.length - 1);
	const Y = v => padT + innerH * (1 - (v - min) / (max - min || 1));
	const pts = data.map((d, i) => [X(i), Y(d.value)]);
	const line = pts.map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
	const fill = `${line} L${X(data.length - 1)} ${padT + innerH} L${padL} ${padT + innerH} Z`;
	const grid = [0, .5, 1].map(t => {
		const y = padT + innerH * (1 - t);
		return `<line class="grid-line" x1="${padL}" y1="${y}" x2="${W}" y2="${y}"/>`;
	}).join('');
	const xlabels = data.map((d, i) => (i % 2 === 0)
		? `<text class="axis-label" x="${X(i)}" y="${H - 6}" text-anchor="middle">${d.label}</text>` : '').join('');
	const dot = `<circle cx="${X(data.length - 1)}" cy="${Y(data[data.length - 1].value)}" r="4" style="fill:${C_BLUE};stroke:#fff" stroke-width="2"/>`;
	const total = summary !== undefined ? summary : data[data.length - 1].value;
	const deltaHtml = delta
		? `<span class="delta ${deltaDir === 'down' ? 'down' : 'up'}"><i class="bi bi-arrow-${deltaDir === 'down' ? 'down' : 'up'}-short"></i> ${delta}</span>`
		: '';
	return `
		<div class="chart-summary"><span class="big">${total}</span>${deltaHtml}<span class="sub">${summaryLabel}</span></div>
		<div class="chart-wrap">
			<svg class="chart-svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid meet">
				${grid}
				<path d="${fill}" style="fill:rgba(42,111,219,0.13);stroke:none"></path>
				<path d="${line}" style="fill:none;stroke:${C_BLUE}" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round"></path>
				${xlabels}${dot}
			</svg>
		</div>`;
}

// Mantissen für niceCeil: jede lässt sich in Halbe und Viertel mit runden Werten teilen
// (z.B. 1.2 → 0.3/0.6/0.9), und der Abstand zwischen zwei Stufen ist klein genug, dass
// das Diagramm immer mindestens ~80 % der Höhe nutzt.
const NICE_MANTISSEN = [1, 1.2, 1.6, 2, 2.4, 3, 4, 5, 6, 8, 10];

/**
 * Rundet auf eine "schöne" Achsen-Obergrenze (Mantisse aus NICE_MANTISSEN × Zehnerpotenz),
 * damit die Y-Achse auf runden Zahlen landet statt auf zufälligen Werten wie 127.
 * @param {number} x
 * @returns {number}
 */
export function niceCeil(x) {
	if (x <= 0) return 1;
	const exp = Math.pow(10, Math.floor(Math.log10(x)));
	const m = x / exp;
	// Toleranz gegen Float-Rundung (120000 / 100000 = 1.2000000000000002 soll 1.2 bleiben).
	return NICE_MANTISSEN.find((niceM) => m <= niceM + 1e-9) * exp;
}

/* ─────────────────── MEHRSERIEN-LINIENDIAGRAMM ─────────────────────────── */
/**
 * Mehrere Linien auf gemeinsamer X-/Y-Achse, mit Legende. Anders als area()
 * für genau eine Serie ist lines() für Vergleiche zweier oder mehr Reihen
 * gedacht (z.B. Soll vs. Ist über die Tage eines Monats).
 *
 * @param {{name:string, color?:string, values:number[], dash?:boolean, legendValue?:number, legendText?:string}[]} series
 *   Jede Serie hat denselben Index-Raum wie `labels` (i-tes Label = i-ter Wert).
 *   `dash:true` zeichnet die Serie gestrichelt – passt für Plan-/Soll-Linien.
 *   `legendText` (String) überschreibt die berechnete Legendenzahl komplett – nützlich,
 *   wenn der Eintrag mehrere Werte zeigen soll (z.B. "bis heute / Gesamt").
 * @param {string[]} labels  X-Achsen-Beschriftungen (z.B. ['1','2',...,'31'])
 * @param {object} [opts]
 * @param {(n:number)=>string} [opts.format=num]   Formatter für Y-Achse (und Fallback
 *   für die Legende, falls kein legendFormat angegeben ist).
 * @param {(n:number)=>string} [opts.legendFormat]  Eigener Formatter für die Legende
 *   (z.B. volle Euro-Darstellung, während die Achse kompakt rendert).
 * @param {string} [opts.summary]                  Große Kennzahl oben links.
 * @param {string} [opts.summaryLabel]
 * @param {number} [opts.labelEvery=5]             Nur jedes N-te X-Label rendern.
 *   Wird ignoriert, wenn `xlabelFilter` gesetzt ist.
 * @param {(label:string, i:number, n:number)=>boolean} [opts.xlabelFilter]
 *   Volle Kontrolle über die sichtbaren X-Labels (z.B. exakt Tage 1/7/14/21/28).
 * @param {number} [opts.markerIndex]              Index für vertikale Markierungslinie
 *   (z.B. "heute" innerhalb des Monats). Wird mit optionalem Label oben gezeichnet.
 * @param {string} [opts.markerLabel='heute']
 * @param {(i:number)=>string} [opts.tooltip]      Wenn gesetzt, wird pro Datenspalte
 *   ein transparenter Hit-Rect mit <title> über das Diagramm gelegt. Beim Hovern
 *   zeigt der Browser den von der Funktion gelieferten Text als nativen Tooltip.
 */
export function lines(series, labels, { format = num, legendFormat, summary, summaryLabel = '', labelEvery = 5, xlabelFilter, markerIndex, markerLabel = 'heute', tooltip } = {}) {
	// padL bemessen für kompakte Y-Achsenlabels wie "10 T €" / "100 T €" (~35 px).
	// Frühere 52 px ließen sichtbar tote Fläche links neben dem Diagramm.
	const W = 320, H = 150, padL = 38, padR = 8, padB = 22, padT = 10;
	const allValues = series.flatMap((s) => s.values).filter((v) => Number.isFinite(v));
	const rawMax = Math.max(...allValues, 0);
	const niceMax = niceCeil(rawMax);
	const innerW = W - padL - padR, innerH = H - padB - padT;
	const n = labels.length;
	const X = (i) => padL + (n > 1 ? (innerW * i) / (n - 1) : innerW / 2);
	const Y = (v) => padT + innerH * (1 - v / niceMax);
	const grid = [0, .25, .5, .75, 1].map((t) => {
		const y = padT + innerH * (1 - t);
		const v = Math.round(niceMax * t);
		return `<line class="grid-line" x1="${padL}" y1="${y}" x2="${W - padR}" y2="${y}"/>
				<text class="axis-label" x="${padL - 4}" y="${y + 3}" text-anchor="end">${format(v)}</text>`;
	}).join('');
	// Bei xlabelFilter entscheidet der Caller pro Label, sonst Default-Verhalten:
	// jedes N-te Label, zzgl. letztes nur, wenn es weit genug vom vorherigen entfernt ist
	// (verhindert Überlappung von z.B. "29" und "30" bei labelEvery=7).
	const lastIdx = n - 1;
	const lastOffset = lastIdx % labelEvery;
	const forceLast = lastOffset !== 0 && lastOffset > Math.floor(labelEvery / 2);
	const showXLabel = xlabelFilter
		? (label, i) => xlabelFilter(label, i, n)
		: (label, i) => i % labelEvery === 0 || (i === lastIdx && forceLast);
	const xlabels = labels.map((label, i) => showXLabel(label, i)
		? `<text class="axis-label" x="${X(i)}" y="${H - 6}" text-anchor="middle">${label}</text>` : '').join('');
	const paths = series.map((s) => {
		const color = s.color || C_BLUE;
		// NaN-Werte (z.B. zukünftige Tage bei Ist-Kurve) brechen die Linie ab statt
		// auf 0 zu fallen: aus der Sequenz wird ein neues "M"-Subpath gestartet.
		let d = '';
		let pendingMove = true;
		s.values.forEach((v, i) => {
			if (!Number.isFinite(v)) { pendingMove = true; return; }
			const x = X(i).toFixed(1);
			const y = Y(v).toFixed(1);
			d += (pendingMove ? `M${x} ${y}` : ` L${x} ${y}`);
			pendingMove = false;
		});
		if (!d) return '';
		const dashAttr = s.dash ? ` stroke-dasharray="4 3"` : '';
		return `<path d="${d}" style="fill:none;stroke:${color}" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"${dashAttr}></path>`;
	}).join('');
	let marker = '';
	if (Number.isFinite(markerIndex) && markerIndex >= 0 && markerIndex < n) {
		const mx = X(markerIndex).toFixed(1);
		// Neutralgrau statt einer Serienfarbe, damit die Markierung sich klar von den
		// Datenlinien (Ist=rot, Soll=blau) absetzt und nicht als zusätzliche Kurve gelesen wird.
		marker = `<line x1="${mx}" y1="${padT}" x2="${mx}" y2="${padT + innerH}" style="stroke:#5b6b7e;stroke-width:1" stroke-dasharray="2 3"></line>
				<text class="axis-label" x="${mx}" y="${padT - 2}" text-anchor="middle">${markerLabel}</text>`;
	}
	const legend = series.map((s) => {
		const color = s.color || C_BLUE;
		const sum = s.values.reduce((acc, v) => Number.isFinite(v) ? acc + v : acc, 0);
		const shown = s.legendValue !== undefined ? s.legendValue : sum;
		const text = s.legendText !== undefined ? s.legendText : (legendFormat ? legendFormat(shown) : format(shown));
		return `<span title="${s.name}"><span class="dot" style="background:${color}"></span>${s.name} <span class="val">${text}</span></span>`;
	}).join('');
	// Transparente Hit-Areas pro Datenspalte: jede Spalte deckt die Hälfte des
	// Wegs zur Nachbarspalte ab. Browser blendet beim Hover das <title>-Element
	// als nativen Tooltip ein – kein JS, keine zusätzliche Styling-Pflege.
	let hitAreas = '';
	if (typeof tooltip === 'function' && n > 0) {
		hitAreas = labels.map((_, i) => {
			const text = tooltip(i);
			if (text == null || text === '') return '';
			const cx = X(i);
			const left = i === 0 ? padL : (X(i - 1) + cx) / 2;
			const right = i === n - 1 ? padL + innerW : (cx + X(i + 1)) / 2;
			const w = right - left;
			return `<rect x="${left.toFixed(1)}" y="${padT}" width="${w.toFixed(1)}" height="${innerH}" style="fill:transparent" pointer-events="all"><title>${text}</title></rect>`;
		}).join('');
	}
	const summaryHtml = summary !== undefined
		? `<div class="chart-summary"><span class="big">${summary}</span><span class="sub">${summaryLabel}</span></div>`
		: '';
	return `
		${summaryHtml}
		<div class="chart-wrap">
			<svg class="chart-svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid meet">
				${grid}${marker}${paths}${xlabels}${hitAreas}
			</svg>
			<div class="chart-legend">${legend}</div>
		</div>`;
}

/* ──────────────────── HORIZONTALE BALKEN (HTML/CSS) ─────────────────────── */
/**
 * @param {{label:string, value:number}[]} data  (idealerweise vorsortiert)
 * @param {object} [opts]
 * @param {string} [opts.unit]          z.B. "h" oder "€"
 * @param {string|number} [opts.summary]
 * @param {string} [opts.summaryLabel]
 * @param {string[]} [opts.palette]
 */
export function hbars(data, { unit = '', summary, summaryLabel = '', palette = PALETTE } = {}) {
	const max = Math.max(...data.map(d => d.value), 1);
	const u = unit ? ` ${unit}` : '';
	const rows = data.map((d, i) =>
		`<div class="hbar-row">
			<div class="hbar-name" title="${d.label}">${d.label.substring(0, 24)}</div>
			<div class="hbar-track"><div class="hbar-fill" style="width:${(d.value / max * 100).toFixed(1)}%;background:${palette[i % palette.length]}"></div></div>
			<div class="hbar-val">${num(d.value)}${u}</div>
		</div>`).join('');
	const total = summary !== undefined ? summary : `${num(data.reduce((s, d) => s + d.value, 0))}${u}`;
	return `
		<div class="chart-summary"><span class="big">${total}</span><span class="sub">${summaryLabel}</span></div>
		<div class="chart-wrap"><div class="hbar-list">${rows}</div></div>`;
}

/* ───────────────────────────── GAUGE (Halbkreis) ───────────────────────── */
/**
 * @param {number} pct  0..100
 * @param {object} [opts]
 * @param {string} [opts.label]
 */
export function gauge(pct, { label = 'Erfüllung' } = {}) {
	const cx = 80, cy = 86, r = 62;
	const a0 = Math.PI, a1 = 0; // 180° → 0°
	const pol = a => [cx + r * Math.cos(a), cy + r * Math.sin(a)];
	const [sx, sy] = pol(a0), [ex, ey] = pol(a1);
	const clamped = Math.max(0, Math.min(100, pct));
	const [vx, vy] = pol(a0 + (a1 - a0) * (clamped / 100));
	const col = clamped >= 90 ? C_GREEN : clamped >= 70 ? C_YELLOW : C_RED;
	return `
		<div class="chart-wrap">
			<svg class="chart-svg" viewBox="0 0 160 110" preserveAspectRatio="xMidYMid meet">
				<path d="M${sx} ${sy} A${r} ${r} 0 0 1 ${ex} ${ey}" style="fill:none;stroke:#eaf1fb" stroke-width="14" stroke-linecap="round"></path>
				<path d="M${sx} ${sy} A${r} ${r} 0 1 1 ${vx} ${vy}" style="fill:none;stroke:${col}" stroke-width="14" stroke-linecap="round"></path>
				<text class="gauge-num" x="80" y="80" text-anchor="middle">${Math.round(clamped)}%</text>
				<text class="gauge-lbl" x="80" y="98" text-anchor="middle">${label}</text>
				<text class="gauge-end" x="${sx}" y="${sy + 14}" text-anchor="middle">0</text>
				<text class="gauge-end" x="${ex}" y="${ey + 14}" text-anchor="middle">100</text>
			</svg>
		</div>`;
}

/* ───────────────────── GESTAPELTE BALKEN (Anteil) ──────────────────────── */
/**
 * @param {{label:string, used:number, total:number}[]} data
 * @param {object} [opts]
 * @param {string} [opts.unit]
 * @param {string} [opts.usedLabel]
 * @param {string} [opts.restLabel]
 * @param {string} [opts.summaryLabel]
 */
export function stacked(data, { unit = 'Tage', usedLabel = 'Genommen', restLabel = 'Verbleibend', summaryLabel = '' } = {}) {
	const usedColor = '#2a6fdb', restColor = '#b9def0';
	const rows = data.map(d => {
		const usedPct = (d.used / (d.total || 1) * 100).toFixed(1);
		return `<div class="stack-row">
			<div class="stack-head" title="${d.label}">
				<span class="nm">${d.label.substring(0, 24)}</span>
				<span class="qt">${d.used} / ${d.total} ${unit}</span>
			</div>
			<div class="stack-track">
				<div class="stack-seg" style="width:${usedPct}%;background:${usedColor}"></div>
				<div class="stack-seg" style="width:${(100 - usedPct).toFixed(1)}%;background:${restColor}"></div>
			</div>
		</div>`;
	}).join('');
	const usedSum = data.reduce((s, d) => s + d.used, 0);
	const totalSum = data.reduce((s, d) => s + d.total, 0) || 1;
	const avgPct = Math.round(usedSum / totalSum * 100);
	return `
		<div class="chart-summary"><span class="big">${avgPct}%</span><span class="sub">${summaryLabel}</span></div>
		<div class="chart-wrap"><div class="stack-list">${rows}</div>
			<div class="chart-legend" style="margin-top:12px">
				<span><span class="dot" style="background:${usedColor}"></span>${usedLabel}</span>
				<span><span class="dot" style="background:${restColor}"></span>${restLabel}</span>
			</div>
		</div>`;
}
